import { useEffect, useState, useMemo, useCallback, RefObject } from 'react'
import Router, { useRouter } from 'next/router'

import CheckupParameters from '../../../CheckupParameters'
import { concatParams, getHashFromUrl, getParams } from 'utils/getURLData'
import { enableDocumentScroll, disableDocumentScroll } from 'utils/handlerDocumentScroll'
import { Events, RouterEvents } from 'constants/events'

const FullScreenStatus = {
  enabled: 'enabled',
  disabled: 'disabled',
}

const PARAM_KEY = 'fullscreen'

const useFullscreen = (containerRef?: RefObject<HTMLDivElement>) => {
  const router = useRouter()
  const initParam = useMemo(() => {
    const queryParams = getParams(router.asPath)
    return queryParams[PARAM_KEY] === FullScreenStatus.enabled
  }, [])

  const [isFullScreen, setIsFullScreen] = useState(false)
  const [dimension, setDimension] = useState({ width: '0px', height: '0px' })

  const getBaseURL = (fullscreen?: string) => {
    const initHash = getHashFromUrl(router.asPath)
    const query = concatParams({ [PARAM_KEY]: fullscreen }) + initHash
    const path = router.pathname.replace('/[id]', '')
    const materialTheme = router?.query?.id ? `/${router?.query?.id}` : ''
    const baseUrl = path + materialTheme
    const url = query ? `${baseUrl}?${query}` : baseUrl
    return url
  }

  const formatURL = (fullscreen?: string) => {
    const url = getBaseURL(fullscreen)

    if (fullscreen === FullScreenStatus.enabled) {
      router.push(url, undefined, { shallow: true })
      return
    }
    router.back()
  }

  const setFullscreen = () => {
    disableDocumentScroll()
    formatURL(FullScreenStatus.enabled)
  }

  const unsetFullscreen = () => {
    enableDocumentScroll()
    formatURL(FullScreenStatus.disabled)
  }

  const toggleFullScreen = useCallback(() => {
    const newState = !isFullScreen
    setIsFullScreen(newState)
    newState ? setFullscreen() : unsetFullscreen()
  }, [isFullScreen])

  useEffect(() => {
    const exitEscFullscreen = (e: KeyboardEvent) => {
      if (isFullScreen && e.key === 'Escape') toggleFullScreen()
    }

    document.addEventListener(Events.KEYDOWN, exitEscFullscreen)

    return () => document.removeEventListener(Events.KEYDOWN, exitEscFullscreen)
  }, [isFullScreen]) //выход из полноэкранного режима по нажатию на esc

  useEffect(() => {
    if (initParam) {
      const url = getBaseURL(FullScreenStatus.disabled)
      router.replace(url, undefined, { shallow: true })
    }
    const handleURLChangeComplete = () => {
      const queryParams = getParams(window.location.href)
      const state = queryParams[PARAM_KEY] === FullScreenStatus.enabled
      setIsFullScreen(state)
    }

    Router.events.on(RouterEvents.ROUTE_CHANGE_COMPLETE, handleURLChangeComplete)
    return () => Router.events.off(RouterEvents.ROUTE_CHANGE_COMPLETE, handleURLChangeComplete)
  }, [])

  useEffect(() => {
    if (!containerRef?.current) return
    const handleResize = () => {
      const containerWidth = containerRef?.current?.offsetWidth
      const containerHeight = containerRef?.current?.offsetHeight

      if (!containerWidth || !containerHeight) return

      if (containerWidth / containerHeight < 16 / 9) {
        setDimension({
          height: `${(containerWidth * 9) / 16}px`,
          width: `${containerWidth}px`,
        })
      } else {
        setDimension({
          height: `${containerHeight}px`,
          width: `${containerHeight / (9 / 16)}px`,
        })

        if (isFullScreen) {
          CheckupParameters.set({
            isFullScreen,
            videoContainer: {
              height: containerHeight,
              width: containerWidth,
            },
          })
        }
      }
    }

    handleResize()
    window.addEventListener(Events.RESIZE, handleResize)
    return () => window.removeEventListener(Events.RESIZE, handleResize)
  }, [isFullScreen])

  return { isFullScreen, dimension, toggleFullScreen }
}

export default useFullscreen
